import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ApplicationForm from './components/ApplicationForm';

export default function LicenseApplicationPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = 'パタカラ®公式施設ライセンス 事前申込み｜株式会社パックスシステムズ';

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      '介護・医療施設向け「パタカラ®」公式施設ライセンスの事前申込みページです。施設情報と利用目的を確認後、登録方法をメールでご案内します。'
    );

    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'パタカラ®公式施設ライセンス 事前申込み｜株式会社パックスシステムズ',
      description: '介護・医療施設向け「パタカラ®」公式施設ライセンスの事前申込みページです。施設情報と利用目的を確認後、登録方法をメールでご案内します。',
      url: `${siteUrl}/license-application`,
      inLanguage: 'ja',
      isPartOf: {
        '@type': 'WebSite',
        name: 'パタカラ®公式施設ライセンス',
        url: `${siteUrl}/`,
      },
    };

    const existing = document.querySelectorAll('script[data-schema="license-application"]');
    existing.forEach((s) => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'license-application');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="license-application"]').forEach((s) => s.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/">
              <img
                src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png"
                alt="パタカラ®ロゴ"
                className="h-12 w-auto object-contain"
              />
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors">ホーム</Link>
            <Link to="/trademark-guidelines" className="text-sm font-medium text-gray-700 hover:text-orange-500 transition-colors">商標利用ガイドライン</Link>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
            >
              お問い合わせ
            </Link>
          </div>
          <button
            className="md:hidden p-2 text-gray-700 cursor-pointer"
            aria-label={mobileMenuOpen ? 'メニューを閉じる' : 'メニューを開く'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="fixed top-[72px] left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg md:hidden">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line text-lg w-5 h-5 flex items-center justify-center text-gray-400"></i>
              ホーム
            </Link>
            <Link
              to="/trademark-guidelines"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-shield-check-line text-lg w-5 h-5 flex items-center justify-center text-gray-400"></i>
              商標利用ガイドライン
            </Link>
            <Link
              to="/contact"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-orange-500 bg-orange-50 cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center text-orange-500"></i>
              お問い合わせ
            </Link>
          </div>
        </div>
      )}

      {/* Hero / 案内文 */}
      <section className="pt-32 pb-12 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-3xl md:text-4xl font-serif font-black text-slate-800 mb-6">
            パタカラ®公式施設ライセンス 事前申込み
          </h1>
          <div className="text-left space-y-4 text-sm md:text-base text-gray-700 leading-relaxed">
            <p>
              パタカラ®公式施設ライセンスへのお申込みをご検討いただき、ありがとうございます。
            </p>
            <p>
              適切な許諾と商標管理のため、株式会社パックスシステムズが施設・事業所の情報および利用内容を事前に確認しています。
            </p>
            <p>
              下記フォームへ必要事項をご入力ください。内容を確認後、担当者よりメールで、公式施設ライセンスのお手続き方法をご案内します。
            </p>
          </div>
          <div className="mt-6 p-5 bg-orange-50/70 rounded-xl border border-orange-100 text-left space-y-2">
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">※このフォームの送信だけでは、ライセンス契約は成立しません。</p>
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">※確認の結果、利用方法について追加でお尋ねする場合があります。</p>
            <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
              ※自治体・官公庁等による非営利の公的利用は、こちらのフォームではなく、
              <Link to="/contact" className="text-orange-600 underline hover:text-orange-700">お問い合わせページ</Link>
              からご相談ください。
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6">
          <ApplicationForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white pt-16 pb-8 mt-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img
                src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png"
                alt="パタカラ®ロゴ"
                className="h-10 w-auto object-contain mb-4 bg-white rounded-lg p-1.5"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                介護施設・医療施設向けの公式ライセンスサービス
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">サービス</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/trademark-guidelines" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">商標利用ガイドライン</Link></li>
                <li><Link to="/terms" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">利用規約</Link></li>
                <li><Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">お問い合わせ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">会社情報</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">株式会社パックスシステムズ</p>
              <p className="text-xs text-gray-500 leading-relaxed">
                担当：関<br />
                〒231-0023<br />
                横浜市中区山下町1番地<br />
                シルクセンターM1F<br />
                TEL: 045-681-3165<br />
                FAX: 045-681-3173<br />
                メール：info@patakara.org
              </p>
            </div>
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">登録商標</h4>
              <p className="text-xs text-gray-500 leading-relaxed">
                登録商標「パタカラ®」<br />
                登録第5657576号
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">© 2026 パタカラ®公式ライセンス. All rights reserved.</p>
            <p className="text-xs text-gray-500">「パタカラ®」は株式会社パックスシステムズの登録商標です（登録第5657576号）。</p>
          </div>
        </div>
      </footer>
    </div>
  );
}